/**
 * /admin/reports — accounting reports hub.
 * @file src/web/features/admin/pages/reports.tsx
 *
 * A categorized launcher (like Tally's Display → More Reports). Active
 * reports link out; the rest are marked "Soon". Everything is scoped to
 * the entity picked in the sidebar.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Scale, ListTree, FileText, Users2,
  Wallet, Layers, ArrowRight, CheckCircle2, AlertTriangle, ShieldCheck, RefreshCw,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { useCompany } from '../lib/company-context';
import { usePeriod } from '../lib/period-context';

interface Report { label: string; desc: string; href?: string; icon: LucideIcon; soon?: boolean }
interface Section { title: string; items: Report[] }

const SECTIONS: Section[] = [
  {
    title: 'Financial statements',
    items: [
      { label: 'Trial Balance', desc: 'Every ledger’s closing balance, Dr = Cr tie-out', href: '/admin/reports/trial-balance', icon: Scale },
      { label: 'Balance Sheet', desc: 'Assets vs liabilities & capital', href: '/admin/reports/balance-sheet', icon: Layers },
      { label: 'Profit & Loss', desc: 'Income vs expenses', href: '/admin/reports/profit-loss', icon: FileText },
    ],
  },
  {
    title: 'Account books',
    items: [
      { label: 'Cash / Bank Book', desc: 'Cash and bank ledger balances', href: '/admin/reports/cash-bank', icon: Wallet },
      { label: 'Registers', desc: 'Sales, Purchase, Journal, Payment, Receipt, Contra', href: '/admin/reports/registers', icon: ListTree },
    ],
  },
  {
    title: 'Statement of accounts',
    items: [
      { label: 'Group Summary', desc: 'Balances rolled up by account group', href: '/admin/reports/group-summary', icon: ListTree },
      { label: 'Receivables', desc: 'Outstanding sundry debtors', href: '/admin/reports/receivables', icon: Users2 },
      { label: 'Payables', desc: 'Outstanding sundry creditors', href: '/admin/reports/payables', icon: Users2 },
    ],
  },
];

export default function ReportsPage() {
  return (
    <>
      <SEO title="Reports" />
      <AdminPageHeader title="Reports" breadcrumbs={[{ label: 'oddits' }, { label: 'Reports' }]} />

      <div className="space-y-8">
        <BooksHealth />
        {SECTIONS.map((section) => (
          <div key={section.title}>
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink-400">{section.title}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {section.items.map((r) => <Card key={r.label} report={r} />)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Books health */
/* -------------------------------------------------------------------------- */

interface Integrity {
  trialBalance: { debit: number; credit: number; difference: number; balanced: boolean };
  balanceSheet: { assets: number; liabilities: number; difference: number; balanced: boolean };
  unknownLedgers: { id: string; name: string; group: string; balance: number }[];
  unclassifiedCount: number;
}

const inr = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Books-health banner on the Reports hub. Surfaces the two accounting
 * invariants (Trial Balance Dr=Cr, Balance Sheet Assets=Liabilities) and any
 * unclassified ledgers that silently drop off the Balance Sheet — with a
 * one-click re-classify that backfills group nature from the Tally inference.
 * Scoped to the sidebar entity + period, like every report.
 */
function BooksHealth() {
  const { entityId } = useCompany();
  const { to } = usePeriod();
  const [data, setData] = useState<Integrity | null>(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminFetchJson<Integrity>('/api/tally-import/integrity', { query: { entityId: entityId ?? undefined, to: to || undefined } })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [entityId, to]);
  useEffect(() => { load(); }, [load]);

  async function reclassify() {
    setFixing(true);
    try { await adminFetch('/api/tally-import/reclassify', { method: 'POST' }); load(); }
    finally { setFixing(false); }
  }

  if (loading || !data) return null;
  // Nothing imported for this scope → nothing to report on.
  const empty = data.trialBalance.debit === 0 && data.balanceSheet.assets === 0 && data.unclassifiedCount === 0;
  if (empty) return null;

  const tbOk = data.trialBalance.balanced;
  const bsOk = data.balanceSheet.balanced;
  const hasUnknown = data.unclassifiedCount > 0;
  const allOk = tbOk && bsOk && !hasUnknown;

  return (
    <div className={`rounded-2xl border p-5 ${allOk ? 'border-ok-200 bg-ok-50/50' : 'border-warn-200 bg-warn-50/50'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className={`grid size-9 place-items-center rounded-xl ${allOk ? 'bg-ok-100 text-ok-600' : 'bg-warn-100 text-warn-600'}`}>
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-ink-900">Books health</p>
            <p className="text-xs text-ink-500">{allOk ? 'All accounting checks pass.' : 'Some checks need attention.'}</p>
          </div>
        </div>
        {hasUnknown && (
          <button onClick={reclassify} disabled={fixing}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3.5 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${fixing ? 'animate-spin' : ''}`} /> Auto-classify groups
          </button>
        )}
      </div>

      <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
        <Check ok={tbOk} label="Trial Balance" detail={tbOk ? `Dr = Cr = ₹${inr(data.trialBalance.debit)}` : `Off by ₹${inr(Math.abs(data.trialBalance.difference))}`} />
        <Check ok={bsOk} label="Balance Sheet" detail={bsOk ? `Assets = Liabilities = ₹${inr(data.balanceSheet.assets)}` : `Off by ₹${inr(Math.abs(data.balanceSheet.difference))}`} />
      </div>

      {hasUnknown && (
        <div className="mt-3 rounded-xl border border-warn-200 bg-paper/70 p-3.5">
          <p className="text-xs font-medium text-warn-700">
            {data.unclassifiedCount} ledger{data.unclassifiedCount > 1 ? 's' : ''} with a balance {data.unclassifiedCount > 1 ? 'are' : 'is'} not classified as asset/liability/income/expense — so {data.unclassifiedCount > 1 ? 'they’re' : 'it’s'} missing from the Balance Sheet.
          </p>
          <ul className="mt-2 space-y-1">
            {data.unknownLedgers.slice(0, 5).map((u) => (
              <li key={u.id} className="flex items-center justify-between text-xs text-ink-600">
                <span className="truncate">{u.name} <span className="text-ink-400">· {u.group}</span></span>
                <span className="tabular-nums text-ink-500">₹{inr(u.balance)}</span>
              </li>
            ))}
            {data.unknownLedgers.length > 5 && <li className="text-xs text-ink-400">+{data.unknownLedgers.length - 5} more</li>}
          </ul>
          <p className="mt-2 text-[11px] text-ink-500">Try “Auto-classify groups”. If any remain, set the group’s nature under its account group.</p>
        </div>
      )}
    </div>
  );
}

function Check({ ok, label, detail }: { ok: boolean; label: string; detail: string }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5">
      {ok
        ? <CheckCircle2 className="h-4.5 w-4.5 shrink-0 text-ok-500" />
        : <AlertTriangle className="h-4.5 w-4.5 shrink-0 text-warn-500" />}
      <div className="min-w-0">
        <p className="text-xs font-semibold text-ink-900">{label}</p>
        <p className="truncate text-[11px] text-ink-500">{detail}</p>
      </div>
    </div>
  );
}

function Card({ report: r }: { report: Report }) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <span className={`grid size-10 place-items-center rounded-xl ${r.soon ? 'bg-ink-100 text-ink-400' : 'bg-highlighter-40 text-inkblack'}`}>
          <r.icon className="h-5 w-5" />
        </span>
        {r.soon
          ? <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-500">Soon</span>
          : <ArrowRight className="h-4 w-4 text-ink-300 transition-transform group-hover:translate-x-0.5 group-hover:text-ink-500" />}
      </div>
      <div className="mt-4">
        <p className="text-sm font-semibold text-ink-900">{r.label}</p>
        <p className="mt-1 text-xs text-ink-500">{r.desc}</p>
      </div>
    </>
  );
  if (r.soon || !r.href) {
    return <div className="cursor-not-allowed rounded-2xl border border-ink-200 bg-paper p-5 opacity-70">{inner}</div>;
  }
  return (
    <Link to={r.href} className="group rounded-2xl border border-ink-200 bg-paper p-5 no-underline transition hover:-translate-y-0.5 hover:border-highlighter hover:shadow-sm">
      {inner}
    </Link>
  );
}
